import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { PageState } from '../../shared/data/PageState';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { toPageError } from '../partners/PartnerListPage';
import { defaultWithdrawalCursorQuery, withdrawalsRepositoryToken } from './repository';
import type { DeliveryAttempt } from './types';
import { maskWithdrawalIdentifier, safeEndpointOrigin, safeMaskedDiagnostic } from './types';
import { CursorPageButtons } from './WithdrawalTimeline';

export function DeliveryAttemptsPanel({
  language,
  withdrawalId,
}: {
  readonly language: TimeLanguage;
  readonly withdrawalId: string;
}) {
  const repository = useRepository(withdrawalsRepositoryToken);
  const { t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [cursor, setCursor] = useState('');
  const [history, setHistory] = useState<readonly string[]>(['']);
  const [index, setIndex] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    (signal) =>
      repository.listDeliveryAttempts(
        withdrawalId,
        { ...defaultWithdrawalCursorQuery, cursor },
        signal,
      ),
    [cursor, refreshKey, repository, withdrawalId],
    (page) => page.items.length === 0 && index === 0,
  );
  return (
    <PageState
      emptyMessage={t('withdrawals.attempts.empty')}
      error={toPageError(resource.error, t('withdrawals.errors.attempts'))}
      onRetry={() => setRefreshKey((value) => value + 1)}
      status={resource.status}
    >
      {resource.data === null ? null : (
        <Box>
          {mobile ? (
            <CompactDeliveryAttempts attempts={resource.data.items} language={language} />
          ) : (
            <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }} tabIndex={0}>
              <Table
                aria-label={t('withdrawals.attempts.label')}
                size="small"
                sx={{ minWidth: 1650 }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      'attemptId',
                      'attemptNumber',
                      'operation',
                      'result',
                      'httpStatus',
                      'retryable',
                      'partnerCreditId',
                      'error',
                      'transferId',
                      'integrationVersion',
                      'endpoint',
                      'startedAt',
                      'completedAt',
                    ].map((field) => (
                      <TableCell key={field}>{t(`withdrawals.attempts.${field}`)}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resource.data.items.map((attempt) => (
                    <TableRow key={attempt.attemptId}>
                      <TableCell>{attempt.attemptId}</TableCell>
                      <TableCell>{attempt.attemptNumber}</TableCell>
                      <TableCell>{attempt.operation}</TableCell>
                      <TableCell>
                        <Chip
                          label={attempt.normalizedResult ?? t('common.notAvailable')}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{attempt.httpStatus ?? t('common.notAvailable')}</TableCell>
                      <TableCell>{retryableLabel(attempt.retryable, t)}</TableCell>
                      <TableCell>{maskWithdrawalIdentifier(attempt.partnerCreditId)}</TableCell>
                      <TableCell>{errorSummary(attempt, t)}</TableCell>
                      <TableCell>{attempt.transferId}</TableCell>
                      <TableCell>{attempt.integrationVersionId}</TableCell>
                      <TableCell>{safeEndpointOrigin(attempt.endpointOrigin)}</TableCell>
                      <TableCell>{withOffset(attempt.startedAt, language)}</TableCell>
                      <TableCell>
                        {attempt.completedAt === null
                          ? t('common.notAvailable')
                          : withOffset(attempt.completedAt, language)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <CursorPageButtons
            index={index}
            nextCursor={resource.data.nextCursor}
            onMove={(nextIndex, nextCursor) => {
              setIndex(nextIndex);
              setCursor(nextCursor);
              if (nextIndex > index)
                setHistory((current) => [...current.slice(0, nextIndex), nextCursor]);
            }}
            previousCursor={history[index - 1] ?? ''}
          />
        </Box>
      )}
    </PageState>
  );
}

function CompactDeliveryAttempts({
  attempts,
  language,
}: {
  readonly attempts: readonly DeliveryAttempt[];
  readonly language: TimeLanguage;
}) {
  const { t } = useTranslation();
  return (
    <Stack
      aria-label={t('withdrawals.attempts.label')}
      component="ul"
      spacing={1}
      sx={{ listStyle: 'none', m: 0, p: 0 }}
    >
      {attempts.map((attempt) => (
        <Paper component="li" key={attempt.attemptId} sx={{ p: 2 }} variant="outlined">
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 700 }}>{attempt.operation}</Typography>
              <Chip label={attempt.normalizedResult ?? t('common.notAvailable')} size="small" />
            </Stack>
            <Typography variant="body2">
              {t('withdrawals.attempts.attemptId')}: {attempt.attemptId} · #{attempt.attemptNumber}
            </Typography>
            <Typography variant="body2">
              {t('withdrawals.attempts.retryable')}: {retryableLabel(attempt.retryable, t)} · HTTP{' '}
              {attempt.httpStatus ?? t('common.notAvailable')}
            </Typography>
            <Typography variant="body2">
              {t('withdrawals.attempts.error')}: {errorSummary(attempt, t)}
            </Typography>
            <Typography variant="body2">
              {t('withdrawals.attempts.partnerCreditId')}:{' '}
              {maskWithdrawalIdentifier(attempt.partnerCreditId)}
            </Typography>
            <Typography variant="body2">
              {t('withdrawals.attempts.transferId')}: {attempt.transferId}
            </Typography>
            <Typography variant="body2">
              {t('withdrawals.attempts.integrationVersion')}: {attempt.integrationVersionId}
            </Typography>
            <Typography variant="body2">
              {t('withdrawals.attempts.endpoint')}: {safeEndpointOrigin(attempt.endpointOrigin)}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {t('withdrawals.attempts.startedAt')}: {withOffset(attempt.startedAt, language)} ·{' '}
              {t('withdrawals.attempts.completedAt')}:{' '}
              {attempt.completedAt === null
                ? t('common.notAvailable')
                : withOffset(attempt.completedAt, language)}
            </Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function retryableLabel(value: boolean | null, t: ReturnType<typeof useTranslation>['t']): string {
  return value === null ? t('common.notAvailable') : t(value ? 'common.yes' : 'common.no');
}

function errorSummary(
  attempt: Pick<DeliveryAttempt, 'errorCode' | 'errorDetail'>,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  return `${attempt.errorCode ?? t('common.notAvailable')} · ${safeMaskedDiagnostic(attempt.errorDetail)}`;
}

function withOffset(value: string, language: TimeLanguage) {
  const time = formatTimestamp(value, 'Asia/Bangkok', language);
  return `${time.display} (${time.originalOffset})`;
}
